/**
 * AnswerContentBlock.jsx
 * ─────────────────────────────────────────────────────────────
 * IJ:       IJ-251 · Phase 5E-3c · Query Thread Render Extraction · ANSWER CONTENT sub-region
 * Date:     2026-08-02
 * Umbrella: IJ-076 · Predictive Text Rebuild continuation · Refactor Programme 2026 · Phase 5E-3
 * Owner:    E1
 *
 * Purpose:
 *   Extract the ANSWER CONTENT sub-region from the KnowledgeGraphPage.jsx
 *   queryThread render tree. Third slice of the five-sub-slice Phase 5E-3
 *   programme. Six sub-regions on screen:
 *     1. Prose wrapper opens · Progress indicator (spinner while the answer
 *        is still "Searching") · ReactMarkdown-rendered answer content ·
 *        prose wrapper closes.
 *     2. Sources footnote (numbered citation list · sibling of prose wrapper).
 *     3. Session 3.6 Answer Recovery card (inline recovery prompt).
 *     4. Export-to-Word icon plus Back-to-top chevron row (hidden for
 *        forecast_prompt type).
 *
 * Props (8):
 *   item                       · one queryThread entry
 *   idx                        · thread-item index (keys every data-testid)
 *   isMobile                   · mobile-vs-desktop layout branch
 *   handleCitationClick        · opens the CitationModal for a numbered citation
 *   handleExportToWord         · click handler on the Export-to-Word icon
 *   handleQuery                · used by the Answer Recovery card 'Retry' button
 *   markdownComponents         · shared config passed through from parent
 *   renderContentWithCitations · shared helper passed through from parent
 *
 * Byte-verbatim on every className · style · data-testid · axios URL ·
 * callback body. Zero behaviour change.
 */
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChevronUp, Download } from 'lucide-react';

export function AnswerContentBlock({
  item,
  idx,
  isMobile,
  handleCitationClick,
  handleExportToWord,
  handleQuery,
  markdownComponents,
  renderContentWithCitations,
}) {
  return (
    <>
                <div className="prose prose-sm prose-invert max-w-none">
                  {/* Progress indicator during query processing */}
                  {item.result.answer && item.result.answer.includes('Searching knowledge graph') && (
                    <div className="flex flex-col items-center py-6 gap-3" data-testid={`query-progress-${idx}`}>
                      <div className="animate-spin h-8 w-8 border-3 border-cyan-500 border-t-transparent rounded-full"></div>
                      <p className="text-gray-300 font-medium text-sm">Searching knowledge graph and web sources...</p>
                      {item.progressSeconds > 0 && (
                        <p className="text-gray-400 text-xs">{item.progressSeconds}s elapsed</p>
                      )}
                      {item.progressMessage && (
                        <p className="text-amber-600 text-xs mt-1">{item.progressMessage}</p>
                      )}
                    </div>
                  )}
                  {/* Actual answer content */}
                  {!(item.result.answer && item.result.answer.includes('Searching knowledge graph')) && (
                  <ReactMarkdown 
                    remarkPlugins={[remarkGfm]} 
                    components={{
                      ...markdownComponents,
                      p: ({children, ...props}) => {
                        const citationMap = item.result.citation_map || {};
                        return (
                          <p {...props}>
                            {React.Children.map(children, child => {
                              if (typeof child === 'string') {
                                return renderContentWithCitations(child, citationMap);
                              }
                              return child;
                            })}
                          </p>
                        );
                      },
                      li: ({children, ...props}) => {
                        const citationMap = item.result.citation_map || {};
                        return (
                          <li {...props}>
                            {React.Children.map(children, child => {
                              if (typeof child === 'string') {
                                return renderContentWithCitations(child, citationMap);
                              }
                              return child;
                            })}
                          </li>
                        );
                      }
                    }}
                  >
                    {item.result.answer}
                  </ReactMarkdown>
                  )}
                </div>

                {/* Sources footnote — lists every numbered citation present in
                    the answer so the user can audit where each claim came
                    from without having to click every pill. Click a row to
                    open the excerpt modal, same as clicking the inline pill. */}
                {!(item.result.answer && item.result.answer.includes('Searching knowledge graph')) && item.result.citation_map && Object.keys(item.result.citation_map).length > 0 && (
                  <div className="mt-5 pt-4 border-t border-gray-400/30" data-testid={`sources-footnote-${idx}`}>
                    <p className="text-[11px] uppercase tracking-wider text-gray-400 mb-2 font-semibold">Sources</p>
                    <ol className="space-y-1.5">
                      {Object.keys(item.result.citation_map)
                        .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
                        .map((citationNum) => {
                          const citation = item.result.citation_map[citationNum];
                          if (!citation) return null;
                          return (
                            <li
                              key={citationNum}
                              onClick={() => handleCitationClick(citationNum, item.result.citation_map)}
                              className="flex items-start gap-2 text-xs text-gray-300 cursor-pointer hover:text-cyan-300 transition-colors group"
                              data-testid={`source-footnote-item-${idx}-${citationNum}`}
                            >
                              <span
                                className="inline-flex items-center justify-center px-1.5 py-0.5 text-[11px] font-semibold rounded-full flex-shrink-0 mt-0.5"
                                style={{
                                  backgroundColor: '#67e8f9',
                                  color: '#0b1220',
                                  border: '1px solid rgba(8,18,32,0.35)',
                                  minWidth: '1.5rem'
                                }}
                              >
                                {citationNum}
                              </span>
                              <span className="flex-1 leading-snug group-hover:underline break-all">
                                {citation.filename || citation.title || citation.url || `Source ${citationNum}`}
                              </span>
                            </li>
                          );
                        })}
                    </ol>
                  </div>
                )}

                {/* Session 3.6 — Answer Recovery card. Appears inline below the
                    answer when the backend flags the response as low-success
                    (negative phrasing and/or no sources). Offers LLM-generated
                    rephrasings the user can click to pivot in one tap, so a
                    "no examples available" dead-end becomes a one-click retry. */}
                {item.result.answer_quality && item.result.answer_quality.found_info === false && (item.result.answer_quality.rephrasings || []).length > 0 && (
                  <div
                    className="mt-4 p-4 rounded-lg border border-amber-400/40 bg-amber-400/5"
                    data-testid={`recovery-prompt-${idx}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-full bg-amber-400/20 border border-amber-400/50 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-3.5 h-3.5 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-100 mb-0.5">Let me try another angle</p>
                        <p className="text-xs text-gray-600 mb-3">Tap any of these to re-run with a sharper phrasing:</p>
                        <div className="flex flex-wrap gap-2">
                          {item.result.answer_quality.rephrasings.map((r, ri) => (
                            <button
                              key={ri}
                              onClick={() => handleQuery(r, true)}
                              className="px-3 py-1.5 rounded-full text-sm font-medium border bg-white/10 border-white/20 text-gray-200 hover:border-amber-500 hover:bg-white/20 transition-colors text-left"
                              data-testid={`recovery-rephrasing-${idx}-${ri}`}
                            >
                              {r}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Export to Word icon - hide for forecast_prompt type */}
                {item.result.type !== 'forecast_prompt' && (
                <div className="mt-4 pt-3 border-t border-gray-400/30 flex justify-end items-center gap-2">
                  {/* Back to top — sits to the LEFT of Export-to-Word so the
                      two no longer overlap (D2 fix, Jun 11 2026). */}
                  <div className="relative group">
                    <button
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className="p-2 text-gray-300 hover:text-cyan-300 transition-colors rounded-lg hover:bg-white/10"
                      data-testid={`back-to-top-btn-${idx}`}
                      aria-label="Back to top"
                    >
                      <ChevronUp className="h-7 w-7" />
                    </button>
                    <span className="absolute -top-9 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-gray-800 text-base text-white rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">Back to Top</span>
                  </div>
                  <div className="relative group">
                    <button onClick={() => handleExportToWord(item)} className="p-2 text-gray-300 hover:text-green-400 transition-colors rounded-lg hover:bg-white/10" data-testid={`export-word-btn-${idx}`}>
                      <Download className="h-7 w-7" />
                    </button>
                    <span className="absolute -top-9 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-gray-800 text-base text-white rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">Export to Word</span>
                  </div>
                </div>
                )}
    </>
  );
}
